import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Indicator } from 'is24-corecss'
import { IconS24EyeOpened24 } from '@is24/cosma-ui-icons'
import { AttachmentDataWithRequiredAttachment, AttachmentType } from '../../../../../../../../@types/types'
import _ from 'lodash'
import styles from './MultimediaDetailsModalContent.module.css'
import ToggleSwitch from '../../../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import uploadedDocumentImage from '../../atachmentsList/document/uploaded_document.svg'
import TitleSection from './titleSection/TitleSection'
import { convertImageUrl } from '../../../../../../../../services/conversion/conversionServices'
import { isWebView } from '../../../../../../../../utils/navigation'

export interface MultimediaNewModalContentAttachmentProps {
  attachment: AttachmentDataWithRequiredAttachment
  index: number
  attachmentsAmount: number
  onTitleChange: (attachment: AttachmentDataWithRequiredAttachment) => void
  onFloorPlanStatusChange: (attachment: AttachmentDataWithRequiredAttachment) => void
  onDelete: () => void
}
const MultimediaDetailsModalContent: React.FC<MultimediaNewModalContentAttachmentProps> = ({
  attachment,
  index,
  attachmentsAmount,
  onTitleChange,
  onFloorPlanStatusChange,
  onDelete,
}) => {
  if (!attachment.attachment) {
    return null
  }

  const { t } = useTranslation()

  const copyAttachment = (attachment: AttachmentDataWithRequiredAttachment): AttachmentDataWithRequiredAttachment =>
    _.cloneDeep(attachment)

  const floorPlanTitle = t('wizard.screen.multimedia.floorplan')

  const onOpenDocumentInNewTab = useCallback(() => {
    window.open(attachment.attachment?.url, '_blank')
  }, [attachment])

  const onIsFloorPlanChanged = useCallback(() => {
    const newAttachment: AttachmentDataWithRequiredAttachment = copyAttachment(attachment)
    onFloorPlanStatusChange(newAttachment)
  }, [attachment, onFloorPlanStatusChange])

  const isFloorPlan = !!attachment.attachment.floorplan
  const imageUrl = convertImageUrl('320', '160', attachment.attachment.url || '')
  const currentAttachmentTitle = isFloorPlan ? floorPlanTitle : attachment.attachment.title
  const isPicture = useMemo(() => attachment.attachmentType === AttachmentType.PICTURE, [attachment])
  const documentStyles = useMemo(() => (!isPicture ? styles.documentImageContainer : ''), [isPicture])
  const isMobile = isWebView()

  return (
    <div className={`${styles.multimediaNewModalContentAttachment}`} data-testid='multimedia-details-modal'>
      <div className={`${styles.topSection}`}>
        <div className={`${isMobile ? styles.mobileImage : styles.image} ${documentStyles} absolute-reference`}>
          {getThumbnailImage(isPicture, imageUrl)}
          <div className={`${styles.imageLabel} grid grid-flex gutter-s`}>
            {isMobile && attachmentsAmount > 1 && (
              <div className='grid-item'>
                <Indicator
                  className={`${isMobile ? styles.mobileIndicator : styles.indicator} font-body-small-bold`}
                  appearance='label'
                  labelColor='white'
                  labelVariant='flat'
                >
                  {t('wizard.screen.multimediaNewModalContent.mobile.pictureFrom', {
                    current: index + 1,
                    amount: attachmentsAmount,
                  })}
                </Indicator>
              </div>
            )}
            <div className='grid-item'>
              {currentAttachmentTitle && (
                <Indicator
                  className={`${isMobile ? styles.mobileIndicator : styles.indicator} font-body-small-bold`}
                  appearance='label'
                  labelColor='white'
                  labelVariant='flat'
                >
                  {currentAttachmentTitle}
                </Indicator>
              )}
            </div>
          </div>
        </div>
        {!isMobile && (
          <div className={`${styles.rightSection} padding-left-m`}>
            <div>
              {t('wizard.screen.multimediaNewModalContent.pictureFrom', {
                current: index + 1,
                amount: attachmentsAmount,
              })}
            </div>
            <div>
              {attachment.attachmentType === AttachmentType.DOCUMENT && (
                <Button
                  appearance='inverted'
                  size='large'
                  iconStandalone={true}
                  className={`${styles.openDocumentButton} margin-right-m palm-hide`}
                  aria-label='OpenDocumentButton'
                  onClick={onOpenDocumentInNewTab}
                >
                  <IconS24EyeOpened24 />
                </Button>
              )}
              <Button
                appearance='inverted'
                size='large'
                iconStandalone={true}
                className={`${styles.trashButton}`}
                aria-label='TrashButton'
                onClick={onDelete}
                data-testid='delete-attachment-button'
              >
                <span className='is24-icon-trash cursor-pointer'></span>
              </Button>
            </div>
          </div>
        )}
      </div>

      <TitleSection attachment={attachment} onTitleChange={onTitleChange} />

      {!isMobile && (
        <div className='padding-top-l padding-bottom-l'>
          <ToggleSwitch
            id='isFloorPlan'
            propertyName='custom'
            value={!!attachment.attachment.floorplan}
            label={t('wizard.screen.multimediaNewModalContent.isFloorPlan', 'The picture shows a floor plan')}
            onChange={onIsFloorPlanChanged}
          />
        </div>
      )}
    </div>
  )
}

function getThumbnailImage(isPicture: boolean, imageUrl: string | undefined) {
  return isPicture ? (
    imageUrl && <img src={imageUrl} alt='Picture' className={styles.pictureImg} />
  ) : (
    <img src={uploadedDocumentImage} alt='PDF' className={`${styles.documentImg} margin-top-m`} />
  )
}

export default MultimediaDetailsModalContent
